"use client";
import { buildImageProps } from "@/lib/sanity/sanityImageUrlBuilder";
/* eslint-disable jsx-a11y/alt-text */

import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import Image from "next/image";
import { useRef } from "react";

import { Project } from "@/lib/types/projects";
import Link from "next/link";
import { Button } from "./ui/button";

gsap.registerPlugin(useGSAP, ScrollTrigger, SplitText);

// Placeholder colours — one per project card (proof of concept)
const PLACEHOLDER_BG_COLORS = [
  "#7C3AED", // violet
  "#DB2777", // pink
  "#0891B2", // cyan
  "#16A34A", // green
  "#D97706", // amber
  "#DC2626", // red
];

interface StackingProjectCardsProps {
  projects: Project[];
}

function StackingProjectCards({ projects }: StackingProjectCardsProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef<HTMLDivElement>(null);
  const projectCardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const bgLayerRefs = useRef<(HTMLDivElement | null)[]>([]);

  useGSAP(
    () => {
      const cards = projectCardRefs.current.filter(Boolean) as HTMLDivElement[];
      if (cards.length < projects.length) return;

      const bgLayers = bgLayerRefs.current.filter(Boolean) as HTMLDivElement[];

      gsap.set(sectionRef.current, {
        height: window.innerHeight * projects.length * 4,
      });
      gsap.set(pinnedRef.current, {
        height: window.innerHeight,
      });
      // All background layers start as invisible circles at the bottom-centre
      gsap.set(bgLayers, { clipPath: "circle(0% at 50% 100%)" });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom bottom",
          scrub: 1.2,
          pin: pinnedRef.current,
          pinType: "transform",
        },
      });

      SplitText.create(".section-heading", {
        type: "chars",
        charsClass: "section-heading-char",
        mask: "chars",
      });

      tl.from(".section-heading-char", {
        y: "100%",
        ease: "circ.out",
        duration: 0.5,
        stagger: 0.05,
      });

      tl.to(".section-heading-char", {
        y: "-110%",
        ease: "circ.in",
        duration: 0.5,
        stagger: 0.05,
      });
      tl.to(".section-heading", { autoAlpha: 0, duration: 0.1 });

      cards.forEach((card, i) => {
        // Expand the background colour outward from the bottom-centre
        if (bgLayers[i]) {
          tl.to(
            bgLayers[i],
            {
              clipPath: "circle(150% at 50% 100%)",
              ease: "circ.inOut",
              duration: 3,
            },
            "-=0.5s",
          );
        }
        gsap.set(`.project-${i}-title`, { autoAlpha: 1 });
        gsap.set(`.project-${i}-description`, { autoAlpha: 1 });
        gsap.set(`.project-${i}-image`, { autoAlpha: 1 });
        gsap.set(`.project-${i}-link`, { autoAlpha: 0, yPercent: 110 });

        const splitTitle = SplitText.create(`.project-${i}-title`, {
          type: "chars",
          charsClass: `project-${i}-title-char`,
          mask: "chars",
        });

        // tl.set(card, { pointerEvents: "auto" });
        tl.from(
          splitTitle.chars,
          {
            y: "100%",
            ease: "circ.out",
            duration: 0.5,
            stagger: 0.05,
          },
          "-=1.2s",
        );

        const splitDescription = SplitText.create(`.project-${i}-description`, {
          type: "lines",
          linesClass: `project-${i}-description-line`,
          mask: "lines",
        });

        tl.from(
          splitDescription.lines,
          {
            y: "100%",
            ease: "circ.out",
            duration: 0.5,
            stagger: 0.1,
          },
          "-=0.5s",
        );

        tl.from(
          `.project-${i}-image`,
          {
            y: "110%",
            ease: "circ.out",
            duration: 0.5,
          },
          "-=1.5s",
        );

        tl.to(
          `.project-${i}-link`,
          {
            yPercent: 0,
            autoAlpha: 1,
            ease: "circ.out",
            duration: 0.5,
          },
          "-=1.5s",
        );

        tl.to(
          `.project-${i}-image`,
          {
            y: "-110%",
            ease: "circ.in",
            duration: 0.5,
            delay: 1.5,
          },
          "-=0.5s",
        );

        tl.to(
          `.project-${i}-link`,
          {
            yPercent: -101,
            ease: "circ.in",
            duration: 0.5,
            delay: 1.5,
            autoAlpha: 0,
          },
          "-=0.5s",
        );

        tl.to(
          splitDescription.lines,
          {
            y: "-110%",
            ease: "circ.in",
            duration: 0.5,
            stagger: 0.1,
            delay: 1.5,
          },
          "-=0.5s",
        );

        tl.to(
          splitTitle.chars,
          {
            y: "-110%",
            ease: "circ.in",
            duration: 0.5,
            stagger: 0.05,
          },
          "-=3.5s",
        );

        // Brief pause between cards (not after the last)
        // if (i < cards.length - 1) {
        //   tl.to({}, { duration: 0.1 });
        // }
      });
    },
    { scope: sectionRef },
  );

  return (
    <div
      className="w-[calc(100%+1.875rem)] xl:w-[calc(100%+6.25rem)] -mx-3.75 xl:-mx-12.5"
      ref={sectionRef}
    >
      <div
        ref={pinnedRef}
        className="pinned-projects relative overflow-hidden w-full mt-16 2xl:mt-18 will-change-transform"
      >
        {/* Background colour layers — inside the viewport-sized pinned element so
            clip-path percentages are relative to the visible area, not the full scroll height */}
        {projects.map((project, index) => (
          <div
            key={`bg-${project._id}`}
            ref={(el) => {
              bgLayerRefs.current[index] = el;
            }}
            className="h-screen absolute inset-0 pointer-events-none"
            style={{
              backgroundColor:
                project.bgColor?.hex ??
                PLACEHOLDER_BG_COLORS[index % PLACEHOLDER_BG_COLORS.length],
              zIndex: index + 1,
            }}
          />
        ))}
        <h2 className="absolute top-1/2 left-0 right-0 z-10 section-heading font-bold text-3xl md:text-4xl xl:text-7xl mx-auto text-center">
          My Work
        </h2>
        {/* Relative container clips cards entering from below */}
        <div className="absolute top-18 left-0 right-0 bottom-0 z-10 overflow-hidden">
          {projects.map((project, index) => (
            <div
              className={`project-${index} absolute inset-0 flex pointer-events-none px-5 xl:px-12.5`}
              style={{
                zIndex: index,
                color: project.fgColor?.hex ?? "#FFFFFF",
              }}
              key={project._id}
              ref={(el) => {
                projectCardRefs.current[index] = el;
              }}
            >
              <div className="flex flex-col gap-6 w-full">
                <div className="flex flex-col flex-1 min-w-0">
                  <h3
                    className={`project-${index}-title font-semibold mb-3 text-3xl leading-[1.2] xl:text-7xl 2xl:mb-3 `}
                  >
                    {project.title}
                  </h3>
                  <div className="w-full flex">
                    {project.description && (
                      <p
                        className={`project-${index}-description text-sm xl:text-xl`}
                      >
                        {project.description}
                      </p>
                    )}
                    <div className="relative flex flex-col">
                      <div className="overflow-hidden">
                        {project.image && (
                          <div className="relative xl:float-right mb-7 mt-0 shrink-0 w-80 h-45 md:w-80 md:h-47 xl:m-7 xl:w-150 xl:h-90 2xl:w-200 2xl:h-120 rounded-xl">
                            <div
                              className={`project-${index}-image absolute inset-0`}
                            >
                              <Image
                                {...buildImageProps(project.image, {
                                  width: 1000,
                                })}
                                sizes="(max-width: 768px) 250px, (max-width: 1024px) 500px, 1000px"
                                fill
                                placeholder="blur"
                                className="rounded-xl outline -outline-offset-1 outline-black/10 dark:outline-white/10"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      {project.links?.url && (
                        <div className="overflow-hidden w-full z-999">
                          <div
                            className={`project-${index}-link pointer-events-auto absolute bottom-0`}
                          >
                            <Button asChild size="2xl">
                              <Link href={project.links.url}>Visit</Link>
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export { StackingProjectCards };
